import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { organizationsAPI, Organization } from '../../services/api';
import { ExpenseLimitCard } from '../expense/ExpenseLimitCard';

const OrganizationSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState<number>(1);
  const [timerTagSelection, setTimerTagSelection] = useState<
    'single' | 'multiple'
  >('multiple');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  const months = [
    { value: 1, label: t('common.months.january') },
    { value: 2, label: t('common.months.february') },
    { value: 3, label: t('common.months.march') },
    { value: 4, label: t('common.months.april') },
    { value: 5, label: t('common.months.may') },
    { value: 6, label: t('common.months.june') },
    { value: 7, label: t('common.months.july') },
    { value: 8, label: t('common.months.august') },
    { value: 9, label: t('common.months.september') },
    { value: 10, label: t('common.months.october') },
    { value: 11, label: t('common.months.november') },
    { value: 12, label: t('common.months.december') },
  ];

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!user?.organization?.id) return;

      try {
        setLoading(true);
        const response = await organizationsAPI.get(user.organization.id);
        setOrganization(response.data.organization);
        setFiscalYearStartMonth(
          response.data.organization.fiscal_year_start_month || 1,
        );
        setTimerTagSelection(
          response.data.organization.timer_tag_selection || 'multiple',
        );
      } catch (err) {
        setError(t('organization.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [user?.organization?.id, t]);

  const handleSave = async () => {
    if (!organization || !(isAdmin || isManager)) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Managers may only change the timer tag policy (server-enforced too).
      const response = await organizationsAPI.update(organization.id, {
        ...(isAdmin ? { fiscal_year_start_month: fiscalYearStartMonth } : {}),
        timer_tag_selection: timerTagSelection,
      });

      setOrganization(response.data.organization);
      setSuccess(t('organization.saveSuccess'));

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(t('organization.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent'
    : 'w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin && !isManager) {
    return (
      <div className={`${cardClass} p-8`}>
        <div className="text-center">
          <svg
            className={`mx-auto h-12 w-12 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h3
            className={`mt-4 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('common.accessDenied')}
          </h3>
          <p
            className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {t('organization.adminOnly')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ambient glow effect */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[120px]" />
        </div>
      )}

      <div>
        <h1
          className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {t('organization.title')}
        </h1>
        <p
          className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
        >
          {t('organization.subtitle')}
        </p>
      </div>

      {error && (
        <div
          className={`px-4 py-3 rounded-xl ${
            isDark
              ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className={`px-4 py-3 rounded-xl ${
            isDark
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}
        >
          {success}
        </div>
      )}

      <div className={`${cardClass} overflow-hidden`}>
        <div
          className={`px-6 py-4 border-b ${isDark ? 'border-slate-700/50 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}
        >
          <h2
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('organization.fiscalYear')}
          </h2>
          <p
            className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {t('organization.fiscalYearDescription')}
          </p>
        </div>

        <div className="p-6">
          <div className="max-w-md">
            <label
              htmlFor="fiscal-year-month"
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('organization.fiscalYearStartMonth')}
            </label>
            <select
              id="fiscal-year-month"
              value={fiscalYearStartMonth}
              onChange={(e) => setFiscalYearStartMonth(Number(e.target.value))}
              disabled={!isAdmin}
              className={`${selectClass} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <p
              className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('organization.fiscalYearHelp')}
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>

      <div className={`${cardClass} overflow-hidden`}>
        <div
          className={`px-6 py-4 border-b ${isDark ? 'border-slate-700/50 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}
        >
          <h2
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('organization.timerTags', { defaultValue: 'Timer tags' })}
          </h2>
          <p
            className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {t('organization.timerTagsDescription', {
              defaultValue:
                'Control how many tags employees can attach when starting the timer.',
            })}
          </p>
        </div>

        <div className="p-6">
          <div className="max-w-md">
            <label
              htmlFor="timer-tag-selection"
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('organization.timerTagSelection', {
                defaultValue: 'Tag selection',
              })}
            </label>
            <select
              id="timer-tag-selection"
              value={timerTagSelection}
              onChange={(e) =>
                setTimerTagSelection(e.target.value as 'single' | 'multiple')
              }
              className={selectClass}
            >
              <option value="multiple">
                {t('organization.timerTagsMultiple', {
                  defaultValue: 'Multiple tags allowed',
                })}
              </option>
              <option value="single">
                {t('organization.timerTagsSingle', {
                  defaultValue: 'One tag only',
                })}
              </option>
            </select>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>

      <ExpenseLimitCard />

      <div className={`${cardClass} overflow-hidden`}>
        <div
          className={`px-6 py-4 border-b ${isDark ? 'border-slate-700/50 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}
        >
          <h2
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('organization.info')}
          </h2>
        </div>

        <div className="p-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt
                className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('organization.name')}
              </dt>
              <dd
                className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {organization?.name || '-'}
              </dd>
            </div>
            <div>
              <dt
                className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('organization.contactEmail')}
              </dt>
              <dd
                className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {organization?.contact_email || '-'}
              </dd>
            </div>
            <div>
              <dt
                className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('organization.billingEmail')}
              </dt>
              <dd
                className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {organization?.billing_email || '-'}
              </dd>
            </div>
            <div>
              <dt
                className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('organization.address')}
              </dt>
              <dd
                className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {organization?.address || '-'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
